// ArtistSwipeProfiler_MUI.jsx
// ✅ Ultra-minimal Tinder-Style (max 10 Fragen)
// Updates (wie gewünscht):
// - Swipe-Card nimmt sauber 100% der verfügbaren Höhe ein (mit Padding/Margin außen)
// - Ergebnis-Zusammenfassung ist NICHT mehr in einer Card (frei / mehr Platz)
// - Neue Faktoren + pro Faktor Text je nach Level (sehr gut / mittel / schlecht) inkl. Tipp
// - Gesamtauswertung ganz oben: Durchschnitt aller Faktor-Progressbars

import React, { memo, useMemo, useRef, useState } from "react";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Container,
  LinearProgress,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const PURPLE = "#7e57c2";

const theme = createTheme({
  palette: { primary: { main: PURPLE } },
  shape: { borderRadius: 18 },
  typography: { fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 22,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 18px 55px rgba(0,0,0,0.10)",
        },
      },
    },
  },
});

const FACTORS = [
  { key: "PERFORMANCE", label: "Performance / Show" },
  { key: "ACQUISITION", label: "Akquise / Strategie" },
  { key: "SOCIAL", label: "Social Media" },
  { key: "FINANCE", label: "Finanzen" },
];

const EMPTY_SCORES = FACTORS.reduce((acc, t) => ((acc[t.key] = 0), acc), {});

function addWeights(prev, w) {
  const next = { ...prev };
  for (const [k, v] of Object.entries(w || {})) {
    const val = (next[k] || 0) + (Number(v) || 0);
    next[k] = Math.max(0, val); // ✅ keine negativen Scores
  }
  return next;
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function card(id, text, weights) {
  return { id, text, weights }; // weights: { YES:{}, NO:{} }
}

const CARDS = [
  card(
    "q1",
    "Ich habe ein klares Angebot (z.B. „90-Minuten-Show + Technik + Pausenmusik“) – nicht nur „ich mache Musik“.",
    { YES: { PERFORMANCE: 2, ACQUISITION: 1 }, NO: { ACQUISITION: 1 } }
  ),

  card(
    "q2",
    "Ich schreibe jede Woche aktiv Veranstalter/Locations/Brands an (mindestens 5 konkrete Nachrichten).",
    { YES: { ACQUISITION: 3 }, NO: { ACQUISITION: -1 } }
  ),

  card(
    "q3",
    "Ich weiß, wer mich buchen soll (z.B. Hochzeiten, Clubs, Firmenfeiern) und habe dafür passende Songs/Show.",
    { YES: { ACQUISITION: 2, PERFORMANCE: 1 }, NO: { ACQUISITION: -1 } }
  ),

  card(
    "q4",
    "Auf meinem Profil steht klar: was ich mache, wo ich auftrete und wie man mich bucht (Link/DM/Email).",
    { YES: { SOCIAL: 2, ACQUISITION: 1 }, NO: { SOCIAL: -1 } }
  ),

  // so wie dein Beispiel: konkret, menschlich
  card(
    "q5",
    "Ich poste manchmal Stories von Proben oder Backstage-Videos von Konzerten.",
    { YES: { SOCIAL: 1, PERFORMANCE: 1 }, NO: { SOCIAL: -1 } }
  ),

  card(
    "q6",
    "Ich habe eine Setlist oder einen groben Ablauf für meine Show (Intro → Hauptteil → Finale).",
    { YES: { PERFORMANCE: 2 }, NO: { PERFORMANCE: -1 } }
  ),

  // JA schlecht
  card(
    "q7",
    "Ich gehe manchmal ohne Vorbereitung auf die Bühne und improvisiere alles.",
    { YES: { PERFORMANCE: -2 }, NO: { PERFORMANCE: 1 } }
  ),

  card(
    "q8",
    "Meine Show hat mindestens 1–2 feste Momente, die immer gut funktionieren (Ansagen, Mitmach-Part, „Signature“-Song).",
    { YES: { PERFORMANCE: 2 }, NO: { PERFORMANCE: -1 } }
  ),

  card(
    "q9",
    "Ich habe ein kurzes Video (30–60s), das zeigt, wie ich live klinge/aussehe, das ich easy verschicken kann.",
    { YES: { PERFORMANCE: 1, ACQUISITION: 2 }, NO: { ACQUISITION: -1 } }
  ),

  // JA schlecht
  card(
    "q10",
    "Wenn ich Veranstalter anschreibe, schicke ich oft keinen Link/Video mit.",
    { YES: { ACQUISITION: -2 }, NO: { ACQUISITION: 1 } }
  ),

  card(
    "q11",
    "Ich mache Follow-ups, wenn keine Antwort kommt (mindestens 1x nach ein paar Tagen).",
    { YES: { ACQUISITION: 2 }, NO: { ACQUISITION: -1 } }
  ),

  card(
    "q12",
    "Ich habe ein einfaches Presskit (Fotos + kurzer Text + Kontakt), das ich als Link schicken kann.",
    { YES: { ACQUISITION: 2 }, NO: { ACQUISITION: -1 } }
  ),

  card(
    "q13",
    "Ich poste ungefähr 1x pro Woche etwas (Reel/Video/Story zählt) – auch wenn’s nicht perfekt ist.",
    { YES: { SOCIAL: 2 }, NO: { SOCIAL: -2 } }
  ),

  // JA schlecht
  card(
    "q14",
    "Ich lösche oder poste nichts, weil ich Angst habe, dass es cringe wirkt.",
    { YES: { SOCIAL: -2 }, NO: { SOCIAL: 1 } }
  ),

  card(
    "q15",
    "Ich zeige mich ab und zu selbst vor der Kamera (kurz sprechen reicht).",
    { YES: { SOCIAL: 2 }, NO: { SOCIAL: -1 } }
  ),

  card(
    "q16",
    "Wenn jemand neu auf mein Profil kommt, findet man schnell Musik/Live-Video (nicht erst nach langem Suchen).",
    { YES: { SOCIAL: 2 }, NO: { SOCIAL: -1 } }
  ),

  card(
    "q17",
    "Ich kenne meinen typischen Preis/Range und nenne ihn ohne mich zu schämen.",
    { YES: { FINANCE: 2, ACQUISITION: 1 }, NO: { FINANCE: -1 } }
  ),

  // JA schlecht
  card(
    "q18",
    "Ich nehme manchmal Gigs an, obwohl es sich nicht lohnt (zu wenig Geld für Aufwand/Anfahrt/Technik).",
    { YES: { FINANCE: -2 }, NO: { FINANCE: 1 } }
  ),

  card(
    "q19",
    "Ich schreibe Einnahmen und Ausgaben irgendwo mit (Notion/Excel/Handy-Notiz reicht).",
    { YES: { FINANCE: 2 }, NO: { FINANCE: -1 } }
  ),

  card(
    "q20",
    "Wenn Geld reinkommt, lege ich etwas für Steuern/Rücklagen weg (auch wenn’s nur grob ist).",
    { YES: { FINANCE: 3 }, NO: { FINANCE: -2 } }
  ),
];



// ✅ dezente Hintergründe (wechseln pro Card)
const BG_PRESETS = [
  { bg: "linear-gradient(180deg, rgba(126,87,194,0.08), rgba(255,255,255,0))", border: "rgba(126,87,194,0.16)" },
  { bg: "linear-gradient(180deg, rgba(59,130,246,0.08), rgba(255,255,255,0))", border: "rgba(59,130,246,0.14)" },
  { bg: "linear-gradient(180deg, rgba(16,185,129,0.08), rgba(255,255,255,0))", border: "rgba(16,185,129,0.14)" },
  { bg: "linear-gradient(180deg, rgba(244,63,94,0.07), rgba(255,255,255,0))", border: "rgba(244,63,94,0.12)" },
];

function getLevel(pct) {
  if (pct >= 67) return "good";
  if (pct >= 34) return "mid";
  return "low";
}

const FACTOR_FEEDBACK = {
  PERFORMANCE: {
    good: {
      title: "Show wirkt buchbar",
      text: "Man versteht schnell, was du live lieferst. Das macht es für andere leicht, „Ja“ zu sagen.",
      tip: "Halte 1–2 feste Momente in jeder Show gleich (z.B. starkes Intro + ein Mitmach-Part). Das bleibt hängen.",
    },
    mid: {
      title: "Gute Basis, noch zu wenig klar",
      text: "Du kannst performen, aber Außenstehende sehen noch nicht sofort den roten Faden und das „Highlight“ deiner Show.",
      tip: "Schreibe: 1 Satz, was deine Show ist + 3 Stichpunkte (Sound, Stimmung, Highlight). Das nutzt du überall.",
    },
    low: {
      title: "Show ist gerade der Engpass",
      text: "Wenn Ablauf und Nutzen unklar sind, wird Akquise schwer, weil niemand genau weiß, was er bucht.",
      tip: "Mach’s simpel: Setlist festlegen + klares Ende + 1 Highlight definieren. Erst dann weiter feilen.",
    },
  },

  ACQUISITION: {
    good: {
      title: "Akquise läuft bei dir",
      text: "Du gehst aktiv auf Veranstalter zu und bekommst dadurch planbarer Anfragen und Gigs.",
      tip: "Mach eine kleine Wochenzahl fix (z.B. 5 neue Kontakte + 5 Follow-ups). Hauptsache konstant.",
    },
    mid: {
      title: "Du machst Akquise, aber unregelmäßig",
      text: "Du kommst voran, nur passiert es nicht stabil genug. Dadurch entstehen Pausen und unnötiger Stress.",
      tip: "Block dir jede Woche 30 Minuten für neue Kontakte. Und block dir jede Woche 30 Minuten nur für Follow-ups.",
    },
    low: {
      title: "Akquise passiert zu wenig",
      text: "Ohne regelmäßige Outreach hängt alles an Zufall, Reichweite oder „irgendwer kennt wen“.",
      tip: "Schreibe dir 20 mögliche Kontakte auf. Danach: jede Woche 5 Nachrichten mit Link/Video verschicken.",
    },
  },

  SOCIAL: {
    good: {
      title: "Social bringt dir etwas",
      text: "Man erkennt auf deinem Profil schnell, wer du bist und was du anbietest. Das baut Vertrauen auf.",
      tip: "Mach zwei Formate fix (z.B. Probe/Backstage + Live-Clip). Wiederholen ist besser als immer neu erfinden.",
    },
    mid: {
      title: "Content ist da, aber ohne Routine",
      text: "Du postest schon, aber nicht so regelmäßig, dass Leute dich sofort einordnen und dranbleiben.",
      tip: "Leg einen einfachen Wochenrhythmus fest (z.B. 1 Clip + 2 Stories). Weniger überlegen, mehr durchziehen.",
    },
    low: {
      title: "Social ist aktuell ein blinder Fleck",
      text: "Wenn kaum was da ist oder es wirkt zufällig, entsteht wenig Vertrauen und selten eine Anfrage.",
      tip: "Starte klein: 1 kurzer Clip pro Woche (20–30 Sekunden) + 1 Satz Kontext + „DM für Booking“.",
    },
  },

  FINANCE: {
    good: {
      title: "Finanzen sind sauber",
      text: "Du hast Überblick über Preise, Einnahmen und Basics. Das schützt dich vor Chaos.",
      tip: "Trenne privat und Musik (separates Konto oder Unterkonto). Das macht alles leichter.",
    },
    mid: {
      title: "Grundlage ok, aber noch unsicher",
      text: "Du hast ein Gefühl dafür, aber Preis/Orga/Rücklagen sind noch nicht stabil geregelt.",
      tip: "Schreib eine Seite: Preisrange, monatliche Kosten, Zielumsatz, wie du Rechnungen machst, wie viel du zurücklegst.",
    },
    low: {
      title: "Finanzen sind gerade ein Risiko",
      text: "Wenn du Geld verdienst ohne klare Regeln (Preis, Rechnung, Steuern), kann es schnell stressig werden.",
      tip: "Mach ein Minimal-Setup: Preisrange festlegen + Einnahmen notieren + 25% Rücklage-Regel.",
    },
  },
};



function pickFocusFromFactor(topKey) {
  const map = {
    PERFORMANCE: {
      name: "Show-Fokus",
      tagline: "Du gewinnst über Erlebnis, Hook und ein klares Paket.",
      hint: "Baue dein Angebot in 2 Pakete + 1 Signature Moment, dann wird Akquise viel leichter.",
    },
    ACQUISITION: {
      name: "Strategie-Fokus",
      tagline: "Du gewinnst über System, Routine und klare Schritte zu Kunden.",
      hint: "Wenn du’s 4 Wochen trackst, ist es plötzlich planbar statt emotional.",
    },
    SOCIAL: {
      name: "Content-Fokus",
      tagline: "Du gewinnst über Formate, Wiederholung und Sichtbarkeit.",
      hint: "Baue Serien, die du ohne Nachdenken produzieren kannst. Konsistenz > Perfektion.",
    },
    FINANCE: {
      name: "Business-Basis-Fokus",
      tagline: "Du gewinnst über Klarheit, Pricing und saubere Grundlagen.",
      hint: "Wenn Zahlen klar sind, kannst du Entscheidungen schneller treffen (und stressfreier wachsen).",
    },
  };
  return map[topKey] || map.PERFORMANCE;
}

const clampLines = (lines) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lines,
  overflow: "hidden",
});


const SwipeCard = memo(function SwipeCard({ text, accent, onChoose }) {
  const ref = useRef(null);
  const start = useRef({ x: 0, y: 0 });
  const drag = useRef({ active: false, dx: 0, dy: 0 });

  const [dx, setDx] = useState(0);
  const [dy, setDy] = useState(0);

  const THRESHOLD = 115;

  const reset = () => {
    drag.current = { active: false, dx: 0, dy: 0 };
    setDx(0);
    setDy(0);
  };

  const commit = (choice) => {
    reset();
    onChoose(choice); // "YES" | "NO"
  };

  const onPointerDown = (e) => {
    ref.current?.setPointerCapture?.(e.pointerId);
    start.current = { x: e.clientX, y: e.clientY };
    drag.current.active = true;
  };

  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const x = e.clientX - start.current.x;
    const y = e.clientY - start.current.y;
    drag.current.dx = x;
    drag.current.dy = y;
    setDx(x);
    setDy(y);
  };

  const onPointerUp = () => {
    const x = drag.current.dx || 0;
    if (Math.abs(x) >= THRESHOLD) commit(x > 0 ? "YES" : "NO");
    else reset();
  };

  const tilt = clamp(dx / 18, -10, 10);

  return (
    <Card
      ref={ref}
      variant="outlined"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={reset}
      sx={{
        // ✅ 100% Höhe (Parent muss fix height haben – setzen wir unten)
        height: "100%",
        minHeight: 0,
        borderRadius: 4,
        overflow: "hidden",
        userSelect: "none",
        touchAction: "pan-y",
        transform: `translateX(${dx}px) translateY(${dy * 0.12}px) rotate(${tilt}deg)`,
        transition: drag.current.active ? "none" : "transform 160ms ease",
        borderColor: accent.border,
        backgroundImage: accent.bg,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.2, sm: 3 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Text mittig, nimmt Platz */}
        <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
          <Typography
            sx={{
              fontWeight: 950,
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
              fontSize: { xs: 22, sm: 26 },
              textAlign: "center",
              px: 0.5,
            }}
          >
            {text}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", pb: 0.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ButtonBase
              onClick={() => commit("NO")}
              sx={(t) => ({
                width: 64,
                height: 64,
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.12)",
                bgcolor: "rgba(255,255,255,0.92)",
                boxShadow: "0 10px 26px rgba(0,0,0,0.10)",
                display: "grid",
                placeItems: "center",
                transition: "transform 120ms ease, border-color 120ms ease",
                "&:active": { transform: "scale(0.96)" },
                "&:hover": { borderColor: alpha(t.palette.text.primary, 0.28) },
              })}
              aria-label="Nein"
            >
              <CloseRoundedIcon sx={{ fontSize: 30, opacity: 0.9 }} />
            </ButtonBase>

            <ButtonBase
              onClick={() => commit("YES")}
              sx={(t) => ({
                width: 70,
                height: 70,
                borderRadius: 999,
                border: `1px solid ${alpha(t.palette.primary.main, 0.22)}`,
                bgcolor: alpha(t.palette.primary.main, 0.10),
                boxShadow: `0 14px 34px ${alpha(t.palette.primary.main, 0.20)}`,
                display: "grid",
                placeItems: "center",
                transition: "transform 120ms ease, background-color 120ms ease",
                "&:active": { transform: "scale(0.96)" },
                "&:hover": { bgcolor: alpha(t.palette.primary.main, 0.14) },
              })}
              aria-label="Ja"
            >
              <CheckRoundedIcon sx={{ fontSize: 36, color: "primary.main" }} />
            </ButtonBase>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
});

export default function ArtistSwipeProfiler_MUI() {
  const total = CARDS.length;

  const [index, setIndex] = useState(0);
  const [scores, setScores] = useState(EMPTY_SCORES);

  const done = index >= total;
  const current = !done ? CARDS[index] : null;

  const progressPct = useMemo(() => Math.round((index / total) * 100), [index, total]);
  const accent = useMemo(() => BG_PRESETS[index % BG_PRESETS.length], [index]);

  const onChoose = (choice) => {
    if (!current) return;
    setScores((prev) => addWeights(prev, current.weights?.[choice]));
    setIndex((prev) => prev + 1);
  };

  // ✅ Max-Punkte pro Faktor dynamisch aus den Fragen berechnen (damit Bars 0–100 “absolut” sind)
  const maxByFactor = useMemo(() => {
    const max = FACTORS.reduce((acc, f) => ((acc[f.key] = 0), acc), {});
    for (const q of CARDS) {
      const yes = q.weights?.YES || {};
      const no = q.weights?.NO || {};
      for (const f of FACTORS) {
        const inc = Math.max(Number(yes[f.key] || 0), Number(no[f.key] || 0));
        max[f.key] += inc;
      }
    }
    // avoid /0
    for (const f of FACTORS) max[f.key] = Math.max(1, max[f.key]);
    return max;
  }, []);

  const factorBars = useMemo(() => {
    return FACTORS.map((f) => {
      const value = Number(scores[f.key] || 0);
      const max = Number(maxByFactor[f.key] || 1);
      const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)));
      return { ...f, value, max, pct };
    });
  }, [scores, maxByFactor]);

  const overallAvg = useMemo(() => {
    const sum = factorBars.reduce((a, b) => a + (Number(b.pct) || 0), 0);
    return Math.round(sum / Math.max(1, factorBars.length));
  }, [factorBars]);

  const topFactor = useMemo(() => {
    const sorted = [...factorBars].sort((a, b) => (b.pct || 0) - (a.pct || 0));
    return sorted[0]?.key || "PERFORMANCE";
  }, [factorBars]);

  const focus = useMemo(() => pickFocusFromFactor(topFactor), [topFactor]);

  const reset = () => {
    setIndex(0);
    setScores(EMPTY_SCORES);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: "100svh",
          bgcolor: "rgba(0,0,0,0.02)",
          px: 2,
          py: 3,
          display: "flex",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.4,
            // ✅ wir brauchen eine echte Höhe, damit die Card 100% nehmen kann
            minHeight: 0,
          }}
        >
          {!done ? (
            <>
              {/* ✅ Card nimmt den ganzen freien Raum ein */}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SwipeCard text={current.text} accent={accent} onChoose={onChoose} />
              </Box>

              {/* Progressbar unten, bleibt klein */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    width: "92%",
                    maxWidth: 520,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: "rgba(0,0,0,0.06)",
                    "& .MuiLinearProgress-bar": { borderRadius: 999 },
                  }}
                />
              </Box>
            </>
        ) : (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: 1.6, // mehr Luft
    }}
  >
    {/* Scrollbarer Bereich (nur innen) */}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        pr: 1,       // mehr innen rechts
        pl: 0.25,
      }}
    >
      {/* Sticky Header: mehr Padding, weniger Rundung */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "rgba(0,0,0,0.02)",
          backdropFilter: "blur(10px)",
          px: { xs: 1.0, sm: 1.4 },
          pt: 1.4,
          pb: 1.3,
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-0.03em",
                  fontSize: { xs: 22, sm: 28 },
                  lineHeight: 1.05,
                }}
              >
                Auswertung
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.55, ...clampLines(2) }} // mehr Raum + notfalls 2 Zeilen
              >
                {focus.name} — {focus.tagline}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                Gesamt
              </Typography>
              <Typography
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-0.02em",
                  fontSize: { xs: 22, sm: 28 },
                  lineHeight: 1.05,
                }}
              >
                {overallAvg}%
              </Typography>
            </Box>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={overallAvg}
            sx={{
              height: 12,            // etwas dicker
              borderRadius: 6,       // weniger rund
              bgcolor: "rgba(0,0,0,0.06)",
              "& .MuiLinearProgress-bar": { borderRadius: 6 },
            }}
          />
        </Stack>

        <Divider sx={{ mt: 1.4 }} />
      </Box>

      {/* Faktoren untereinander: weniger Rundung, mehr Padding */}
      <Stack spacing={1.4} sx={{ pt: 1.6, pb: 1.6, px: { xs: 0.4, sm: 0.8 } }}>
        {factorBars.map((f) => {
          const level = getLevel(f.pct);
          const fb = FACTOR_FEEDBACK[f.key]?.[level];

          return (
            <Card
              key={f.key}
              variant="outlined"
              sx={{
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: { xs: 2.4, sm: 2.9 } }}>
                <Stack spacing={1.3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography sx={{ fontWeight: 950, fontSize: { xs: 15, sm: 16 } }}>
                      {f.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      {f.pct}%
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={f.pct}
                    sx={{
                      height: 10,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.06)",
                      "& .MuiLinearProgress-bar": { borderRadius: 1 },
                    }}
                  />

                  <Box sx={{ pt: 0.4 }}>
                    <Typography sx={{ fontWeight: 900, ...clampLines(2) }}>
                      {fb?.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.6, ...clampLines(4) }}
                    >
                      {fb?.text}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1.4,
                        borderRadius: 1,
                        border: "1px solid rgba(0,0,0,0.10)",
                        bgcolor: "rgba(0,0,0,0.03)",
                        p: { xs: 1.4, sm: 1.6 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 950, display: "block", letterSpacing: "0.02em" }}
                      >
                        Tipp
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.45, ...clampLines(4) }}
                      >
                        {fb?.tip}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>

    {/* Button unten: größer + weniger rund */}
    <Box sx={{ display: "flex", justifyContent: "center", pb: 0.3 }}>
      <ButtonBase
        onClick={reset}
        sx={{
          px: 3.0,
          py: 1.25,
          borderRadius: 10,
          border: "1px solid rgba(0,0,0,0.14)",
          bgcolor: "rgba(255,255,255,0.92)",
          fontWeight: 900,
        }}
      >
        Nochmal
      </ButtonBase>
    </Box>
  </Box>
)}
    </Container>
      </Box >
    </ThemeProvider >
  );
}
